import { alpha, Divider, Grid, Typography } from "@mui/material";
import { Header } from "~/components/DetailsPage/components/Header";
import { Space } from "~/lib/types";
import { Flex } from "~/components/Flex";
import { LoadingButton } from "~/components/loading-button";
import { ScrollToTop } from "~/lib/scroll-to-top";
import { LoadingFailed } from "~/components/loading";
import { EmptySection } from "~/components/vulnerabilities";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useTicketDetailsPage } from "~/pages/tickets/ticket/hooks/useTicketDetailsPage";
import { TicketTickets } from "~/pages/tickets/ticket/TicketPage/TicketTickets/TicketTickets";
import { TicketDetailsBreadcrumbs } from "~/pages/tickets/ticket/TicketPage/TicketDetailsBreadcrumbs/TicketDetailsBreadcrumbs";
import { TicketHeaderTags } from "~/pages/tickets/ticket/TicketPage/TicketHeaderTags/TicketHeaderTags";
import { TicketActivitiesSection } from "~/pages/tickets/ticket/TicketPage/TicketActivitiesSection/TicketActivitiesSection";
import { TicketFindingsSection } from "~/pages/tickets/ticket/TicketPage/TicketFindingsSection/TicketFindingsSection";
import {
  TicketExposedAssets,
  TicketMitigatedAssets,
} from "~/pages/tickets/ticket/TicketPage/TicketAssetsSection";
import { setDocumentTitle } from "~/lib/commonUtils";

type Props = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
};

export const TicketPage = ({ scope, space }: Props) => {
  const {
    caseDetails,
    isLoading,
    isCloseCaseLoading,
    handleCloseCase,
    findings,
    affectedAssetsProps,
    mitigatedAssetsProps,
    tickets,
    caseError,
    totalRefsCount,
    closedRefsCount,
  } = useTicketDetailsPage({ space, scope });

  setDocumentTitle([caseDetails?.title, "Ticketing", scope.name]);

  if (caseError || (!caseDetails && !isLoading)) {
    return <LoadingFailed what="Ticket" />;
  }

  return (
    <Grid container>
      <ScrollToTop />
      <TicketDetailsBreadcrumbs
        scope={scope}
        title={caseDetails?.title || ""}
      />
      <Grid size={12}>
        <Header
          id="ticket-title"
          title={caseDetails?.title || ""}
          created={caseDetails?.createdAt}
          space={space}
          createdBy={caseDetails?.createdBy}
          lastModified={
            caseDetails?.status !== "CLOSED"
              ? caseDetails?.modifiedAt
              : undefined
          }
          tags={
            <TicketHeaderTags
              totalAssets={caseDetails?.assetsCount || 0}
              closedAt={
                caseDetails?.status === "CLOSED"
                  ? caseDetails?.modifiedAt
                  : undefined
              }
            />
          }
          rightSection={
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 4 }} />
              {caseDetails?.status === "OPEN" ? (
                <LoadingButton
                  variant="outlined"
                  loading={isCloseCaseLoading}
                  color="primary"
                  onClick={handleCloseCase}
                  data-testid="close-case"
                  buttonText="Close Ticket"
                  sx={{
                    background: (theme) =>
                      alpha(theme.palette.primary.main, 0.2),
                    borderColor: (theme) => theme.palette.primary.main,
                    color: "text.primary",
                  }}
                />
              ) : (
                <Flex justifyContent="flex-end">
                  <Typography color="text.disabled" fontSize={13}>
                    CLOSED
                  </Typography>
                </Flex>
              )}
            </>
          }
        />
      </Grid>
      <TicketTickets tickets={tickets} />
      <TicketActivitiesSection
        totalRefs={totalRefsCount}
        fixedRefs={closedRefsCount}
      />
      {findings.length > 0 && totalRefsCount > 0 && (
        <TicketFindingsSection
          space={space}
          pageType="AllSecurityFindings"
          findings={findings}
          scope={scope}
          loading={isLoading}
        />
      )}
      {totalRefsCount > 0 ? (
        <TicketExposedAssets {...affectedAssetsProps} />
      ) : (
        <Grid size={12}>
          <EmptySection
            id="no-affected-assets"
            text="All assets that were appended to this case have been deleted."
          />
        </Grid>
      )}
      {totalRefsCount > 0 && (
        <TicketMitigatedAssets {...mitigatedAssetsProps} />
      )}
    </Grid>
  );
};
